import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, Button, Grid, CircularProgress, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  FormControl, InputLabel, Select, MenuItem, Switch, FormControlLabel
} from '@mui/material';
import axios from 'axios';
import { API_BASE_URL, getAuthHeaders } from '../../services/api';
const API_URL = API_BASE_URL;

// Use a default color scheme if styleGuide is not available
const colors = {
  tableHeaderBackground: '#f5f5f5',
  lightBackground: '#f9f9f9',
  success: '#4caf50',
  error: '#f44336',
  info: '#2196f3',
  warning: '#ff9800',
  disabledBackground: '#f5f5f5'
};


const ChartOfAccounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [accountTypes, setAccountTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentAccount, setCurrentAccount] = useState(null);
  const [formData, setFormData] = useState({
    account_code: '',
    name: '',
    account_type_id: '',
    parent_account_id: '',
    description: '',
    is_active: true
  });
  
  // Fetch account types and chart of accounts
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Fetch account types
        const typesResponse = await axios.get(`${API_URL}/accounting/account-types`, {
          headers: getAuthHeaders()
        });
        setAccountTypes(typesResponse.data);
        
        // Fetch chart of accounts
        const accountsResponse = await axios.get(`${API_URL}/accounting/chart-of-accounts`, {
          headers: getAuthHeaders()
        });
        setAccounts(accountsResponse.data);
      } catch (err) {
        console.error('Error fetching accounting data:', err);
        setError('Failed to load chart of accounts. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  const handleOpenDialog = (account = null) => {
    if (account) {
      // Edit mode
      setEditMode(true);
      setCurrentAccount(account);
      setFormData({
        account_code: account.account_code,
        name: account.name,
        account_type_id: account.account_type_id,
        parent_account_id: account.parent_account_id || '',
        description: account.description || '',
        is_active: account.is_active
      });
    } else {
      // Add mode
      setEditMode(false);
      setCurrentAccount(null);
      setFormData({
        account_code: '',
        name: '',
        account_type_id: '',
        parent_account_id: '',
        description: '',
        is_active: true
      });
    }
    setOpenDialog(true);
  };
  
  const handleCloseDialog = () => {
    setOpenDialog(false);
  };
  
  const handleInputChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'is_active' ? checked : value
    });
  };
  
  const handleSubmit = async () => {
    // Validate form
    if (!formData.account_code || !formData.name || !formData.account_type_id) {
      setError('Account code, name, and type are required');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      if (editMode) {
        // Update existing account
        await axios.put(
          `${API_URL}/accounting/chart-of-accounts/${currentAccount.id}`,
          formData,
          { headers: getAuthHeaders() }
        );
      } else {
        // Create new account
        await axios.post(`${API_URL}/accounting/chart-of-accounts`, formData, {
          headers: getAuthHeaders()
        });
      }
      
      // Refresh accounts list
      const response = await axios.get(`${API_URL}/accounting/chart-of-accounts`, {
        headers: getAuthHeaders()
      });
      setAccounts(response.data);
      
      // Close dialog
      handleCloseDialog();
    } catch (err) {
      console.error('Error saving account:', err);
      setError(err.response?.data?.error || 'Failed to save account. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Group accounts by category for better display
  const groupedAccounts = accounts.reduce((groups, account) => {
    const category = account.category || 'other';
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(account);
    return groups;
  }, {});
  
  // Order of categories for display
  const categoryOrder = ['asset', 'liability', 'equity', 'revenue', 'expense', 'other'];
  
  // Get parent account options (exclude the current account in edit mode)
  const parentAccountOptions = accounts
    .filter(account => !editMode || account.id !== currentAccount?.id)
    .sort((a, b) => a.account_code.localeCompare(b.account_code));
  
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" component="h2">
          Chart of Accounts
        </Typography>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={() => handleOpenDialog()}
        >
          Add Account
        </Button>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {loading && !accounts.length ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {categoryOrder.map(category => {
            if (!groupedAccounts[category] || groupedAccounts[category].length === 0) return null;
            
            return (
              <Grid item xs={12} key={category}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    mb: 1, 
                    textTransform: 'capitalize',
                    color: category === 'asset' ? colors.success :
                           category === 'liability' ? colors.warning :
                           category === 'equity' ? colors.info :
                           category === 'revenue' ? colors.success :
                           category === 'expense' ? colors.error :
                           'inherit'
                  }}
                >
                  {category} Accounts
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: colors.tableHeaderBackground }}>
                        <TableCell>Account Code</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Parent Account</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {groupedAccounts[category].map((account) => (
                        <TableRow 
                          key={account.id} 
                          hover
                          sx={{ 
                            backgroundColor: !account.is_active ? colors.disabledBackground : 'inherit',
                            opacity: !account.is_active ? 0.7 : 1
                          }}
                        >
                          <TableCell>{account.account_code}</TableCell>
                          <TableCell>{account.name}</TableCell>
                          <TableCell>{account.account_type_name}</TableCell>
                          <TableCell>{account.parent_account_name || '-'}</TableCell>
                          <TableCell>
                            {account.is_active ? 
                              <span style={{ color: colors.success }}>Active</span> : 
                              <span style={{ color: colors.error }}>Inactive</span>
                            }
                          </TableCell>
                          <TableCell>
                            <Button 
                              size="small" 
                              color="primary"
                              variant="outlined"
                              onClick={() => handleOpenDialog(account)}
                            >
                              Edit
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            );
          })}
        </Grid>
      )}
      
      {/* Add/Edit Account Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editMode ? 'Edit Account' : 'Add New Account'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                name="account_code"
                label="Account Code"
                value={formData.account_code}
                onChange={handleInputChange}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="name"
                label="Account Name"
                value={formData.name}
                onChange={handleInputChange}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel id="account-type-label">Account Type</InputLabel>
                <Select
                  labelId="account-type-label"
                  name="account_type_id"
                  value={formData.account_type_id}
                  onChange={handleInputChange}
                  label="Account Type"
                >
                  {accountTypes.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.name} ({type.category})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="parent-account-label">Parent Account</InputLabel>
                <Select
                  labelId="parent-account-label"
                  name="parent_account_id"
                  value={formData.parent_account_id}
                  onChange={handleInputChange}
                  label="Parent Account"
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {parentAccountOptions.map((account) => (
                    <MenuItem key={account.id} value={account.id}>
                      {account.account_code} - {account.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="description"
                label="Description"
                value={formData.description}
                onChange={handleInputChange}
                fullWidth
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleInputChange}
                    color="primary"
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="primary"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : (editMode ? 'Update' : 'Add')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChartOfAccounts;
